/**
 * @fileoverview Landing page business costs card with mock data.
 * Shows a static breakdown of business costs.
 * @module features/landing/components/LandingBusinessCostsCard
 */

import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'

/**
 * Props for LandingBusinessCostsCard component
 */
interface LandingBusinessCostsCardProps {
  className?: string
}

/**
 * Mock business costs data (matching LandingMetrics - total 47,100)
 */
const MOCK_COSTS = [
  { key: 'shipping', value: 15800 },
  { key: 'warehouse', value: 10900 },
  { key: 'paymentFees', value: 7900 },
  { key: 'software', value: 6000 },
  { key: 'accounting', value: 4000 },
  { key: 'officeTools', value: 2500 },
]

const TOTAL = MOCK_COSTS.reduce((sum, c) => sum + c.value, 0)

/**
 * Formats a number as compact currency
 */
function formatMoney(value: number): string {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K zł`
  }
  return `${value} zł`
}

/**
 * Landing page business costs card.
 * Displays a static breakdown of business costs.
 *
 * @component
 */
export function LandingBusinessCostsCard({ className }: LandingBusinessCostsCardProps): ReactElement {
  const { t } = useTranslation('features-landing')

  return (
    <Card className={cn('glass-card !p-0 overflow-hidden h-fit', className)}>
      {/* Header */}
      <div className="flex items-center gap-2 px-6 py-4 bg-black/10">
        <div
          className="h-2.5 w-2.5 rounded-full shrink-0"
          style={{ backgroundColor: 'var(--chart-legend-overhead)' }}
        />
        <h3 className="text-lg font-semibold text-foreground">{t('demo.businessCosts.title')}</h3>
      </div>

      {/* Cost Items */}
      <div className="border-t border-border/30">
        {MOCK_COSTS.map((cost, idx) => (
          <div
            key={idx}
            className="flex items-center justify-between gap-4 px-6 py-3 border-b border-border/30 last:border-b-0"
          >
            <span className="text-sm text-foreground/70">{t(`demo.businessCosts.${cost.key}`)}</span>
            <span className="font-mono text-sm font-medium">{formatMoney(cost.value)}</span>
          </div>
        ))}
      </div>

      {/* Total Footer */}
      <div className="flex items-center justify-between px-6 pt-2 pb-3 bg-black/10 border-t border-border">
        <div className="flex items-center gap-2">
          <div
            className="h-2.5 w-2.5 rounded-full shrink-0"
            style={{ backgroundColor: 'var(--chart-legend-overhead)' }}
          />
          <span className="text-sm text-foreground/80">{t('demo.labels.total')}</span>
        </div>
        <span className="font-mono text-sm font-semibold text-foreground/70">
          {formatMoney(TOTAL)}
        </span>
      </div>
    </Card>
  )
}
